/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useContext,
  useRef,
  useEffect,
  createContext,
  ReactElement,
} from 'react';
import { css, jsx } from '@emotion/core';
import { interactionContext as _interactionContext } from './interactionContext';
import { setScore, getScore } from 'services/scoring';
import { unlockInteraction } from 'services/unlockInteraction';
import Feedback from 'components/lib/Feedback';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
// import {
//   setMultichoiceData,
//   getMultichoiceData,
// } from 'services/scorm/scormService';
// import {
//   setMultichoiceData,
//   getMultichoiceData,
//   getCMIInteractionsId,
//   setCMIInteractionsMultichoice,
// } from 'services/scorm/scormService';
import getAllInteractions from 'services/apiHelpers/getAllInteractionsOfType';
import { useDispatch } from 'react-redux';
import { updateScoreAction, addScormResponseAction } from 'store/next';

enum SubmitState {
  Initial = 0,
  Resolved = 2,
}

interface MultichoiceProps {
  children: ReactElement[] | ReactElement | string | null;
}

interface MultichoiceContext {
  selected: number | string[];
  updateSelected: (optionId: number | string) => void;
  correct: number | string[];
  isMultiSelect: boolean;
  answerCount: number;
  isLive: boolean;
  submit: SubmitState;
  onSubmit: () => void;
}

const MultichoiceContext = createContext<MultichoiceContext>(undefined);

// keeping this local to the session
// if the interaction is set to score &&
// if the score is not undefined ( has value )
// true
const sessionLock = {};

const Multichoice = (props: MultichoiceProps) => {
  const interactionContext = useContext(_interactionContext);

  const { id: interactionContextId } = interactionContext;
  let {
    numberOfAttempts,
    correctOptions = '',
    score,
    useFeedback = true,
    useMediaInFeedback = true,
    useUniqueOptionFeedback,
    showAttemptNumber,
    showCorrectAnswer,
  } = interactionContext.props;

  // state (s)
  const [selected, setSelected] = useState([]);
  const [submit, setSubmit] = useState(
    sessionLock[interactionContextId]
      ? SubmitState.Resolved
      : SubmitState.Initial
  );
  const [feedback, setFeedback] = useState(false);

  sessionLock[interactionContextId] =
    score && getScore(interactionContextId) !== undefined;

  // console.log('getScore(interactionContextId)', getScore(interactionContextId));

  const dispatch = useDispatch();

  const { children } = props;
  const Question = children[0];
  const Answers = children[2];
  const SubmitBtn = children[1];

  // get the feedbackBlock checking if it has the Attempt feedback
  const hasAttemptFeedback = findByKeyVal_V2(
    interactionContextId,
    'type',
    'Attempt'
  );

  // refs
  const allMultichoiceInteractions = useRef(null);
  const multichoice = useRef(null);
  const attemptNumber = useRef(0);
  const isCorrect = useRef(false);

  // correctOptions array from string
  correctOptions = correctOptions
    .split(/,/g)
    .filter(entry => entry !== '')
    .sort();

  // Correct contains an array of ids of correct answers
  const correct = correctOptions.map(
    (o, i) => Answers.props.children[o - 1]?.props?.id
  );
  const isMultiCorrect = correct.length > 1;
  const isLive = selected.length && submit === 0;

  // Toggle options
  const updateSelected = optionId => {
    let _selected;

    if (isMultiCorrect) {
      if (selected.includes(optionId)) {
        // Toggle off
        _selected = selected.filter(o => optionId !== o);
      } else {
        // Toggle on
        _selected = [...selected, optionId];
      }
    } else {
      _selected = selected[0] === optionId ? [] : [optionId];
    }

    // Maintain "correct" state
    isCorrect.current = _selected.sort().join() === correct.sort().join();

    // console.log({ optionId, _selected });

    // updating setScore for old ways ( display results )
    setScore(interactionContextId, isCorrect.current);
    //
    //

    setSelected(_selected);
  };

  const showFeedback = correct => {
    useFeedback && setFeedback(true);

    if (window.dev()) {
      return;
    }

    if (score && (correct || attemptNumber.current === numberOfAttempts)) {
      const response = selected
        .map(s => {
          let result = '-1';
          Answers.props.children.forEach((a, i) => {
            if (s === a.props.id) {
              result = `${i + 1}`;
              return;
            }
          });
          return result;
        })
        .sort()
        .join(',');

      const action = {
        id: interactionContextId,
        type: 'fill-in',
        reportable: 'multichoice' as 'multichoice',
        response,
        result: correct,
      };

      dispatch(updateScoreAction(interactionContextId, correct));
      dispatch(addScormResponseAction(action));
    }

    const unlock =
      isCorrect.current || attemptNumber.current === numberOfAttempts;

    if (unlock) {
      setSubmit(SubmitState.Resolved);
      unlockInteraction(interactionContextId);
    }
  };

  const onSubmit = () => {
    attemptNumber.current += 1;
    showFeedback(isCorrect.current);
  };

  const onClose = () => {
    // const unlock =
    //   isCorrect.current || attemptNumber.current === numberOfAttempts;

    // !window.dev() && unlock && setSubmit(SubmitState.Resolved);
    setFeedback(false);
    //

    // unlock && unlockInteraction(interactionContextId);
  };

  // effects
  useEffect(() => {
    if (
      parent.multichoiceIsLive &&
      parent.multichoiceIsLive[interactionContextId]
    ) {
      parent.multichoiceIsLive[interactionContextId]({
        isLive: !!isLive,
        isCorrect: isCorrect.current,
      });
    }

    if (parent.showFeedback) {
      parent.showFeedback[interactionContextId] = correct => {
        if (useUniqueOptionFeedback && !isMultiCorrect && !isLive) {
          return alert('No options have been selected');
        }

        if (correct === null) {
          return setSelected([]);
        }

        if (correct === 'attempt') {
          // attemptNumber.current = 1;
          // showFeedback((isCorrect.current = false));
          // return;
        }

        if (feedback) {
          setFeedback(false);
          requestAnimationFrame(() =>
            showFeedback((isCorrect.current = correct))
          );
          //
          return;
        }

        showFeedback((isCorrect.current = correct));
      };
    }
  });

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const question = multichoice.current.querySelector('.chameleon-text')
      .innerText;
    const content = Array.from(
      multichoice.current.querySelectorAll('label')
    ) as any;

    const answerIds = Answers.props.children.map(
      (answer: any) => answer.props.id
    );

    const answers = content.map((t: any) => t.innerText);

    const alphabetized = selected.map((entry: any) => {
      return 'abcdefghijklmnopqrstuvwxyz'.split('')[answerIds.indexOf(entry)];
    });

    // const data = {
    //   id: getCMIInteractionsId(interactionContextId),
    //   question,
    //   type: 'choice',
    //   answers,
    //   response: selected.length ? alphabetized.sort().join(',') : 'neutral',
    //   result: isCorrect.current,
    // };

    // setCMIInteractionsMultichoice(data);
  });

  let parsedIsCorrect;
  if (!useUniqueOptionFeedback && attemptNumber.current < numberOfAttempts) {
    parsedIsCorrect = isCorrect.current || 'attempt';
    // console.log(parsedIsCorrect);
  } else {
    parsedIsCorrect = isCorrect.current;
  }

  // console.log();

  return (
    <MultichoiceContext.Provider
      value={{
        isMultiSelect: isMultiCorrect,
        selected,
        isLive,
        correct,
        updateSelected,
        submit,
        answerCount: Answers?.props?.children?.length || 1,
        onSubmit: () => {
          attemptNumber.current += 1;
          showFeedback(isCorrect.current);
        },
      }}
    >
      <form ref={multichoice} onSubmit={e => e.preventDefault()}>
        {Question}
        {Answers}
        {SubmitBtn}
      </form>

      {feedback && (
        <Feedback
          // get this nested ternary bizniz out of here
          // if() it all above return
          id={
            !isMultiCorrect
              ? !useUniqueOptionFeedback
                ? interactionContextId
                : selected[0]
              : interactionContextId
          }
          // correct={
          //   !window.dev()
          //     ? !isCorrect.current &&
          //       (numberOfAttempts > 1 &&
          //         attemptNumber.current < numberOfAttempts)
          //       ? hasAttemptFeedback
          //         ? 'attempt'
          //         : false
          //       : isCorrect.current
          //     : isCorrect.current
          // }
          correct={window.dev() ? isCorrect.current : parsedIsCorrect}
          useAttemptFeedback={!useUniqueOptionFeedback}
          attempt={attemptNumber.current}
          // numberOfAttempts={
          //   attemptNumber.current < numberOfAttempts ? numberOfAttempts : 0
          // }
          numberOfAttempts={numberOfAttempts}
          showAttemptNumber={showAttemptNumber}
          useMediaInFeedback={useMediaInFeedback}
          onClose={() => {
            const tick = setInterval(() => {
              const submitButtonElement: any = document.querySelector(
                `#i${SubmitBtn?.props?.id}`
              );

              if (submitButtonElement) {
                submitButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            onClose();
          }}
          {...interactionContext.props}
        />
      )}
    </MultichoiceContext.Provider>
  );
};

export default Multichoice;
export { MultichoiceContext };
